/**
 * Everything the robot lanes draw, normalised into one shape.
 *
 * Three sources, one `LaneItem` each, and never two items for one job:
 *
 *  - LIVE tasks (`TaskState`, the live list) -- actual start, actual or
 *    estimated end, and the controls.
 *  - COMPLETIONS (`task_completions`) -- finished tasks the live list no
 *    longer holds, for a day older than its retention. A completion whose
 *    task is still live is dropped: the live record has the controls.
 *  - OCCURRENCES of schedules -- the plan. An occurrence that already
 *    produced a task we hold is dropped in favour of that task, so a past
 *    day shows what HAPPENED, at the times it happened, and only the
 *    future is drawn as the plan. Its schedule's id goes onto the task
 *    (`scheduleId`): the task names its schedule only by the name it had
 *    when it fired, which a rename or a retime's split can break.
 *
 * AUTOMATIC WORK IS LEFT OUT (`isAutomaticTask`). RMF sends robots to
 * charge when idle or low, and returns them to the dock on its own; none
 * of that was asked for by a person or a schedule, and a supervisor
 * planning the day reads it as noise. The test is the requester: our
 * dispatch route and the schedule runner always stamp `booking.requester`
 * (`triggered_by`), and RMF's own tasks carry none -- the same fact the
 * Tasks table's "Triggered By: API" cell is built on. Platform actors
 * (`system:*`) are left out with them.
 *
 * Pure: no React, no fetching. The phone views to come reuse it.
 */
import { hasNoRoute, noRouteWords, type ClosedLiftScope } from '../../../state/lift-rule-waiting';
import type { TaskState } from '../../../api/rmf-client';
import type {
  ScheduleFireStatus,
  ScheduleOccurrence,
  TaskCompletionRecord,
  TaskSchedule,
} from '../../../api/v1';
import {
  CATEGORY_LABEL,
  categoryOf,
  type CalendarLane,
  type LanePaint,
} from '../calendar-categories';
import { cleanModeOfRequest, cleanModeOfTask, toCleanMode } from '../clean-modes';
import { locationFromPhases } from '../task-location';
import { adaptCompletion } from '../older-tasks';
import { overlaps, type TimeWindow } from './lane-time';
import { requesterLabel } from './people';
import { isHighPriority } from '../../../api/task-priority';

export type LaneOutcome =
  /** Finished. Solid. */
  | 'done'
  /** Running now. Solid with a ring. */
  | 'running'
  /** Running, but RMF reports it in `error`/`blocked` -- someone should look. */
  | 'attention'
  /** Interrupted (paused) by an operator or fire mode. */
  | 'paused'
  /** In a robot's queue; start and end are a projection. Outline. */
  | 'queued'
  /** A schedule occurrence that has not come due. Dashed outline. */
  | 'scheduled'
  /** An occurrence that fired, whose task we no longer hold. */
  | 'dispatched'
  /** Did not finish: failed, or a scheduled run that never went out. */
  | 'failed'
  /** Did not finish: cancelled. */
  | 'cancelled'
  /** Not run on purpose: a maintenance window was open. */
  | 'skipped';

/** The outcomes counted (and coloured) as "didn't finish". A
 *  maintenance skip is not one: nothing went wrong, somebody closed the
 *  site on purpose (the same call the month view makes). */
export const DIDNT_FINISH: ReadonlySet<LaneOutcome> = new Set<LaneOutcome>(['failed', 'cancelled']);

// In the words of the people reading it (2026-10-31): "the fleet", never
// "RMF", and "blocked time", the name the calendar gives what the api
// calls a maintenance window.
export const OUTCOME_LABEL: Record<LaneOutcome, string> = {
  done: 'Done',
  running: 'Running now',
  attention: 'Running — needs a look',
  paused: 'Paused',
  queued: 'Queued',
  scheduled: 'Scheduled',
  dispatched: 'Sent to the fleet',
  failed: 'Didn’t finish',
  cancelled: 'Cancelled',
  skipped: 'Skipped — blocked time',
};

/** What each outcome means, in a sentence (the panel and the phone
 *  sheet both say it). */
export const OUTCOME_NOTE: Partial<Record<LaneOutcome, string>> = {
  running: 'In progress now. Its end is the fleet’s estimate.',
  attention: 'Still running, but the fleet reports a problem — someone should look.',
  paused: 'Paused. Resume or cancel it from the task.',
  queued:
    'Waiting in the robot’s queue. Where it sits here is an estimate: the fleet can reorder a queue.',
  scheduled:
    'Sent to the fleet at its start time. Drawn 30 minutes long: a schedule has a start, not a length.',
  dispatched: 'Sent to the fleet. The task it became is no longer in the record this screen reads.',
  skipped: 'It came due during blocked time, so it was not sent.',
};

/** The note for a queued task the fleet has not given a robot yet: no
 *  robot's queue holds it, so "Waiting in the robot's queue" said one
 *  did under a robot line reading "Not assigned yet" (review BC-F16,
 *  2026-10-31). */
export const QUEUED_UNASSIGNED_NOTE = 'Waiting for a robot to take it.';

/** The note a panel shows for an item that did not fail: its outcome's,
 *  with a queued task that has no robot told apart. */
export function outcomeNote(
  item: Pick<LaneItem, 'outcome' | 'robotKey' | 'robotName' | 'detail' | 'waitingNote'>,
): string | null | undefined {
  if (item.outcome === 'queued' && item.robotKey === null && item.robotName === null) {
    return QUEUED_UNASSIGNED_NOTE;
  }
  if (item.waitingNote) return item.waitingNote;
  return OUTCOME_NOTE[item.outcome] ?? item.detail;
}

export type LaneSource =
  | { kind: 'schedule'; name: string; scheduleId?: string }
  | { kind: 'calendar'; who: string }
  | { kind: 'favourite'; name: string }
  | { kind: 'person'; who: string };

export interface LaneItem {
  /** Waiting while a lift is blocked (`lift-rule-waiting.ts`). */
  waitingNote?: string;
  /** Unique across all items: a task id, or `occ:<schedule>:<original>`. */
  id: string;
  kind: 'task' | 'occurrence';
  taskId?: string;
  /** Exactly one of these three is set. */
  live?: TaskState;
  completion?: TaskCompletionRecord;
  occurrence?: ScheduleOccurrence;
  /** `<fleet>/<robot>`, or null when no robot is known (yet). */
  robotKey: string | null;
  robotName: string | null;
  fleet: string | null;
  lane: CalendarLane;
  /** Drawn as something other than its lane: `linen` for a delivery run
   *  by a linen robot (`buildLaneData` sets it). Absent = its lane. */
  paint?: 'linen';
  typeLabel: string;
  place: string | null;
  /** What the bar and the panel are titled. */
  title: string;
  startMs: number;
  /** The TRUE end. A bar may be drawn wider (`MIN_BAR_PX`); this is not. */
  endMs: number;
  /** Start or end is a projection (queued, running, scheduled). */
  estimated: boolean;
  outcome: LaneOutcome;
  source: LaneSource | null;
  /** A task a schedule fired: that schedule's id, from the fire ledger
   *  (the occurrence whose `task_id` is this task). The task itself says
   *  only `calendar:<name>`, the name the schedule had then. Absent when
   *  the ledger in hand does not name it (`scheduleOfItem`). */
  scheduleId?: string;
  /** The runner's or RMF's own words about a bad outcome. */
  detail: string | null;
  /** Marked High (2026-10-31): a planned run by its own level
   *  (`occurrenceIsHigh`), a live task by `TaskSummary.priority`. Absent
   *  for Normal, and for a finished task the record no longer holds:
   *  a completion row carries no priority. */
  high?: true;
}

/** What `item` is drawn as: its paint, else its lane. */
export function paintOf(item: Pick<LaneItem, 'lane' | 'paint'>): LanePaint {
  return item.paint ?? item.lane;
}

const MIN = 60_000;
/** How long a queued task is assumed to take when RMF gave no estimate. */
export const DEFAULT_QUEUED_MS = 15 * MIN;
/** How long an occurrence is drawn: its rule has a start and no length. */
export const OCCURRENCE_MS = 30 * MIN;

/** True for work nobody asked for -- see the module comment. */
export function isAutomaticTask(triggeredBy: string | null | undefined): boolean {
  const by = (triggeredBy ?? '').trim();
  return by === '' || by.startsWith('system:');
}

export function sourceOf(triggeredBy: string | null | undefined): LaneSource | null {
  const by = (triggeredBy ?? '').trim();
  if (!by) return null;
  if (by.startsWith('calendar:')) return { kind: 'calendar', who: by.slice('calendar:'.length) };
  if (by.startsWith('favorite:')) return { kind: 'favourite', name: by.slice('favorite:'.length) };
  return { kind: 'person', who: by };
}

export function sourceText(s: LaneSource | null): string {
  if (!s) return '—';
  switch (s.kind) {
    case 'schedule':
      return `Schedule · ${s.name}`;
    case 'calendar':
      return `Calendar · ${s.who}`;
    case 'favourite':
      return `Favourite · ${s.name}`;
    case 'person':
      // Never a bare subject id (`requesterLabel`, 2026-10-31).
      return `Booked by ${requesterLabel(s.who)}`;
  }
}

export function robotKeyOf(
  fleet: string | null | undefined,
  robot: string | null | undefined,
): string | null {
  const r = (robot ?? '').trim();
  if (!r) return null;
  return `${(fleet ?? '').trim()}/${r}`;
}

/** Resolves a bare robot name to the one known `<fleet>/<robot>` key it
 *  names, when the record carried no fleet. Ambiguous or unknown names
 *  keep an empty fleet (`/NomNom`), which still files consistently. */
export type RobotNameIndex = ReadonlyMap<string, readonly string[]>;

export function indexRobotNames(keys: Iterable<string>): RobotNameIndex {
  const out = new Map<string, string[]>();
  for (const key of keys) {
    const name = key.slice(key.indexOf('/') + 1);
    const list = out.get(name) ?? [];
    if (!list.includes(key)) list.push(key);
    out.set(name, list);
  }
  return out;
}

function resolveKey(
  fleet: string | null | undefined,
  robot: string | null | undefined,
  names: RobotNameIndex,
): string | null {
  const key = robotKeyOf(fleet, robot);
  if (!key || (fleet ?? '').trim()) return key;
  const known = names.get((robot ?? '').trim());
  return known && known.length === 1 ? known[0]! : key;
}

function fleetOfKey(key: string | null): string | null {
  if (!key) return null;
  const f = key.slice(0, key.indexOf('/'));
  return f || null;
}

export function titleOf(typeLabel: string, place: string | null): string {
  return place ? `${typeLabel} · ${place}` : typeLabel;
}

function liveOutcome(t: TaskState): LaneOutcome {
  switch (t.status) {
    case 'completed':
      return 'done';
    case 'failed':
      return 'failed';
    case 'cancelled':
      return 'cancelled';
    case 'interrupted':
      return 'paused';
    case 'queued':
      return 'queued';
    case 'active':
      return t.attention ? 'attention' : 'running';
  }
}

function finite(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

/** A live task's start and end, or null when it is queued (placed later
 *  by `projectQueues`). */
function liveTimes(
  t: TaskState,
  nowMs: number,
): { start: number; end: number; estimated: boolean } | null {
  const started = t.startedAt ? Date.parse(t.startedAt) : NaN;
  const terminal = t.status === 'completed' || t.status === 'failed' || t.status === 'cancelled';
  if (Number.isFinite(started)) {
    if (terminal) {
      const end = finite(t.completedAtMs)
        ? t.completedAtMs
        : finite(t.durationSec)
          ? started + t.durationSec * 1000
          : started;
      return { start: started, end: Math.max(started, end), estimated: false };
    }
    // Running or paused: it ends no earlier than now.
    let end = nowMs;
    if (finite(t.estimatedRemainingSec)) end = nowMs + t.estimatedRemainingSec * 1000;
    else if (finite(t.estimatedDurationSec))
      end = Math.max(nowMs, started + t.estimatedDurationSec * 1000);
    return { start: started, end: Math.max(started, end), estimated: true };
  }
  if (terminal) {
    // Ended without starting (cancelled in the queue, no robot bid): a
    // point at the moment it ended, which is when it stopped being work.
    const at = finite(t.completedAtMs)
      ? t.completedAtMs
      : finite(t.submittedAtMs)
        ? t.submittedAtMs
        : NaN;
    if (!Number.isFinite(at)) return null;
    return { start: at, end: at, estimated: false };
  }
  if (t.status === 'queued') return null;
  // Active with no start: RMF has not said when. Treat as starting now.
  return { start: nowMs, end: nowMs + DEFAULT_QUEUED_MS, estimated: true };
}

/**
 * Whether a planned run goes out High: the occurrence's own level -- the
 * api-server reads the override's task if the run has one, else the
 * schedule's, as the runner sends it -- and the schedule's task only from
 * an api-server older than that field (0.6.56). Both through the one
 * reading rule, so an old 1-5 value is Normal.
 */
export function occurrenceIsHigh(
  o: Pick<ScheduleOccurrence, 'priority'>,
  schedule: Pick<TaskSchedule, 'task'> | undefined,
): boolean {
  return isHighPriority(
    o.priority !== undefined && o.priority !== null ? o.priority : schedule?.task?.priority,
  );
}

function fromLive(
  t: TaskState,
  nowMs: number,
  names: RobotNameIndex,
  liftClosed: readonly ClosedLiftScope[] | null,
): LaneItem | null {
  if (isAutomaticTask(t.triggeredBy)) return null;
  const times = liveTimes(t, nowMs);
  const lane = categoryOf(t.type, cleanModeOfTask(t));
  const robotKey = resolveKey(t.assignedFleet, t.assignedRobot, names);
  const place = locationFromPhases(t)?.text ?? null;
  const typeLabel = CATEGORY_LABEL[lane];
  const noRoute = hasNoRoute(t) ? noRouteWords(liftClosed) : null;
  const waiting = noRoute?.liftBlocked ? noRoute.sentence : null;
  return {
    id: t.id,
    kind: 'task',
    taskId: t.id,
    live: t,
    robotKey,
    robotName: t.assignedRobot?.trim() || null,
    fleet: fleetOfKey(robotKey),
    lane,
    typeLabel,
    place,
    title: titleOf(typeLabel, place),
    // A queued task's slot is filled in by `projectQueues`.
    startMs: times?.start ?? NaN,
    endMs: times?.end ?? NaN,
    estimated: times?.estimated ?? true,
    outcome: liveOutcome(t),
    source: sourceOf(t.triggeredBy),
    // RMF's line names a destination, not a lift: while a lift is blocked the
    // console says that instead. It keeps its slot and its outcome (a `standby`
    // task is queued); only the words change.
    ...(waiting ? { waitingNote: waiting } : {}),
    detail:
      waiting ??
      t.attention?.reason ??
      t.failureReason ??
      (t.synthetic ? 'Closed by the platform; no robot reported it.' : null),
    ...(isHighPriority(t.priority) ? { high: true as const } : {}),
  };
}

function fromCompletion(r: TaskCompletionRecord, names: RobotNameIndex): LaneItem | null {
  if (isAutomaticTask(r.triggered_by)) return null;
  const older = adaptCompletion(r);
  const end = r.completed_at_ms;
  const start = finite(r.started_at_ms) ? r.started_at_ms : end;
  const lane = categoryOf(r.category, older.cleanMode);
  const robotKey = resolveKey(r.assigned_fleet, r.assigned_robot, names);
  const place = older.location?.text ?? null;
  const typeLabel = CATEGORY_LABEL[lane];
  const outcome: LaneOutcome =
    r.state === 'completed' ? 'done' : r.state === 'cancelled' ? 'cancelled' : 'failed';
  return {
    id: r.task_id.task,
    kind: 'task',
    taskId: r.task_id.task,
    completion: r,
    robotKey,
    robotName: r.assigned_robot?.trim() || null,
    fleet: fleetOfKey(robotKey),
    lane,
    typeLabel,
    place,
    title: titleOf(typeLabel, place),
    startMs: start,
    endMs: Math.max(start, end),
    estimated: false,
    outcome,
    source: sourceOf(r.triggered_by),
    detail: r.synthetic ? 'Closed by the platform; no robot reported it.' : null,
  };
}

const OCCURRENCE_OUTCOME: Record<ScheduleFireStatus, LaneOutcome> = {
  dispatched: 'dispatched',
  failed: 'failed',
  missed: 'failed',
  skipped_fire_mode: 'failed',
  skipped_maintenance: 'skipped',
};

function fromOccurrence(
  o: ScheduleOccurrence,
  schedule: TaskSchedule | undefined,
  names: RobotNameIndex,
): LaneItem {
  // The pin: the occurrence's own (an api-server with the calendar-lanes
  // fields reads the override's task too), else the schedule's request.
  const hasOwn = o.fleet !== undefined || o.robot !== undefined;
  const fleet = hasOwn ? o.fleet : schedule?.task?.fleet;
  const robot = hasOwn ? o.robot : schedule?.task?.robot;
  const robotKey = resolveKey(fleet, robot, names);
  const mode =
    toCleanMode(o.clean_mode) ??
    (o.category === 'clean' && schedule ? cleanModeOfRequest(schedule.task) : undefined);
  const lane = categoryOf(o.category, mode);
  return {
    id: `occ:${o.schedule_id}:${o.original_start_ms}`,
    kind: 'occurrence',
    occurrence: o,
    taskId: o.task_id ?? undefined,
    robotKey,
    robotName: robot?.trim() || null,
    fleet: (fleet ?? '').trim() || null,
    lane,
    typeLabel: CATEGORY_LABEL[lane],
    place: null,
    title: o.name,
    startMs: o.start_ms,
    endMs: o.start_ms + OCCURRENCE_MS,
    estimated: true,
    outcome: o.status ? OCCURRENCE_OUTCOME[o.status] : 'scheduled',
    source: { kind: 'schedule', name: o.name, scheduleId: o.schedule_id },
    detail: o.detail ?? null,
    ...(occurrenceIsHigh(o, schedule) ? { high: true as const } : {}),
  };
}

/**
 * Where each queued task will run, as far as anyone can say: one after
 * another in its robot's queue, from the end of whatever that robot is
 * doing now (or from now), High jobs first and otherwise in the order
 * they were submitted. Unassigned queued tasks start now. Every one is
 * `estimated`, and the panel says so -- RMF can reorder a queue, and
 * nothing here pretends otherwise.
 *
 * HIGH FIRST (review PRIO-R5, 2026-10-31): from worker 0.4.40 RMF runs a
 * waiting High job ahead of the waiting Normal ones (the local real-RMF
 * run: queued last, started first). Drawn in submission order, a job
 * with a High badge sat later than it would run, against the badge's own
 * meaning. A live task is High only once a worker that sends priority
 * projects it, so before that this is submission order, as it was.
 */
export function projectQueues(items: LaneItem[], nowMs: number): LaneItem[] {
  const busyUntil = new Map<string, number>();
  for (const it of items) {
    if (!it.robotKey || it.outcome === 'queued') continue;
    if (it.outcome === 'running' || it.outcome === 'attention' || it.outcome === 'paused') {
      busyUntil.set(it.robotKey, Math.max(busyUntil.get(it.robotKey) ?? nowMs, it.endMs));
    }
  }
  const queued = items
    .filter((it) => it.outcome === 'queued' && it.live)
    .sort(
      (a, b) =>
        Number(b.high === true) - Number(a.high === true) ||
        (a.live!.submittedAtMs ?? 0) - (b.live!.submittedAtMs ?? 0) ||
        a.id.localeCompare(b.id),
    );
  const placed = new Map<string, LaneItem>();
  for (const it of queued) {
    const dur =
      finite(it.live!.estimatedDurationSec) && it.live!.estimatedDurationSec! > 0
        ? it.live!.estimatedDurationSec! * 1000
        : DEFAULT_QUEUED_MS;
    const start = it.robotKey ? Math.max(nowMs, busyUntil.get(it.robotKey) ?? nowMs) : nowMs;
    const end = start + dur;
    if (it.robotKey) busyUntil.set(it.robotKey, end);
    placed.set(it.id, { ...it, startMs: start, endMs: end, estimated: true });
  }
  return items.map((it) => placed.get(it.id) ?? it);
}

export interface LaneItemSources {
  /** The lift scopes closed now, for what a waiting task says. */
  liftClosed?: readonly ClosedLiftScope[] | null;
  live: readonly TaskState[];
  completions?: readonly TaskCompletionRecord[];
  occurrences?: readonly ScheduleOccurrence[];
  schedules?: readonly TaskSchedule[];
  /** Every robot key the screen knows, for resolving bare names. */
  robotKeys?: Iterable<string>;
  window: TimeWindow;
  nowMs: number;
}

/**
 * The items that overlap `window`, from all three sources, de-duplicated
 * and with queues projected. A zero-length item (a task cancelled in its
 * queue) counts when its instant is inside the window.
 */
export function buildLaneItems(src: LaneItemSources): LaneItem[] {
  const names = indexRobotNames(src.robotKeys ?? []);
  const schedules = new Map((src.schedules ?? []).map((s) => [s.schedule_id, s]));
  const out: LaneItem[] = [];
  const taskIds = new Set<string>();
  const tasks = new Map<string, LaneItem>();

  for (const t of src.live) {
    taskIds.add(t.id);
    const it = fromLive(t, src.nowMs, names, src.liftClosed ?? null);
    if (!it) continue;
    out.push(it);
    tasks.set(t.id, it);
  }
  for (const r of src.completions ?? []) {
    const id = r.task_id.task;
    if (taskIds.has(id)) continue;
    taskIds.add(id);
    const it = fromCompletion(r, names);
    if (!it) continue;
    out.push(it);
    tasks.set(id, it);
  }
  for (const o of src.occurrences ?? []) {
    // The run it produced is drawn instead -- unless that run was
    // automatic (it never is: the runner stamps a requester) or is not
    // in hand, in which case the occurrence keeps the slot. The run
    // keeps the ledger's word on which schedule fired it.
    if (o.task_id && taskIds.has(o.task_id)) {
      const fired = tasks.get(o.task_id);
      if (fired) fired.scheduleId = o.schedule_id;
      continue;
    }
    out.push(fromOccurrence(o, schedules.get(o.schedule_id), names));
  }

  const projected = projectQueues(out, src.nowMs);
  return projected.filter((it) => {
    if (!Number.isFinite(it.startMs) || !Number.isFinite(it.endMs)) return false;
    if (it.endMs === it.startMs)
      return it.startMs >= src.window.fromMs && it.startMs < src.window.toMs;
    return overlaps({ fromMs: it.startMs, toMs: it.endMs }, src.window);
  });
}
